import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { ActiveSubscription, findActiveSubscription, getActiveSubscriptions } from '../api/Subscription';
import { SUBSCRIPTION_REFRESH_KEY } from '../constants/subscription';
import { AppContext } from '../context/AppContext';
import ensureError from '../utils/ensureError';
const SUBSCRIPTION_REFRESH_TTL_MS = 15 * 60 * 1000;

type UseActiveSubscriptionOptions = {
  poll?: boolean;
  tryLimit?: number;
  delayMs?: number;
};

export default function useActiveSubscription(options: UseActiveSubscriptionOptions = {}) {
  const { poll = false, tryLimit = 10, delayMs = 2500 } = options;

  const { activeSubscription, setActiveSubscription, subscriptionsDisabled } = useContext(AppContext) as {
    activeSubscription: ActiveSubscription | undefined;
    setActiveSubscription: (sub: ActiveSubscription | undefined) => void;
    subscriptionsDisabled: boolean;
  };

  const [fetchedSubscriptions, setFetchedSubscriptions] = useState(false);
  const [isPolling, setIsPolling] = useState<boolean>(poll);
  const [error, setError] = useState<Error | null>(null);

  const tries = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearPollTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const consumeRefreshRequest = (): boolean => {
    try {
      const raw = window.localStorage.getItem(SUBSCRIPTION_REFRESH_KEY);
      if (!raw) return false;

      // Clear regardless to keep it one-shot
      window.localStorage.removeItem(SUBSCRIPTION_REFRESH_KEY);

      const requestedAt = Number(raw);
      if (!Number.isFinite(requestedAt)) return false;

      return Date.now() - requestedAt <= SUBSCRIPTION_REFRESH_TTL_MS;
    } catch {
      return false;
    }
  };

  // Fetch once (always)
  const fetchSubs = useCallback(async (): Promise<void> => {
    try {
      const res = await getActiveSubscriptions();

      if (res?.active_subscriptions) {
        const found = findActiveSubscription(res.active_subscriptions);
        setActiveSubscription(found);
      }
    } catch (err) {
      setError(ensureError(err));
    } finally {
      setFetchedSubscriptions(true);
    }
  }, [setActiveSubscription]);

  // Initial fetch:
  // - first login (no cached activeSubscription)
  // - or user has just returned from Stripe (one-shot refresh flag)
  useEffect(() => {
    if (subscriptionsDisabled) {
      setFetchedSubscriptions(true);
      return;
    }

    const shouldRefresh = consumeRefreshRequest();

    if (!activeSubscription || shouldRefresh) {
      void fetchSubs();
      return;
    }

    setFetchedSubscriptions(true);
  }, [subscriptionsDisabled, activeSubscription, fetchSubs]);

  // Optional polling (only if poll = true)
  useEffect(() => {
    if (!poll) return; // default behaviour: exit early
    if (subscriptionsDisabled) return setIsPolling(false);
    if (activeSubscription) return setIsPolling(false);

    const pollLoop = async () => {
      if (activeSubscription) {
        clearPollTimeout();
        setIsPolling(false);
        return;
      }

      if (tries.current >= tryLimit) {
        clearPollTimeout();
        setIsPolling(false);
        setError(new Error('Subscription did not become active in time.'));
        return;
      }

      tries.current += 1;
      await fetchSubs();

      timeoutRef.current = setTimeout(() => {
        void pollLoop();
      }, delayMs);
    };

    if (!subscriptionsDisabled) void pollLoop();

    return () => {
      clearPollTimeout();
    };
  }, [poll, activeSubscription, subscriptionsDisabled, tryLimit, delayMs, fetchSubs]);

  return {
    activeSubscription,
    fetchedSubscriptions,
    isPolling,
    error,
  };
}
